// =============================================================================
// ShareDiaryModal.tsx — T-258. Manager+ shares a site diary entry to clients.
//
// Flow:
//   1. Load entry + project + client + photos + share history.
//   2. Manager picks contacts from client_contacts (multi-select).
//   3. Toggle CC-self (default ON — manager gets a copy).
//   4. Optional milestone callout from project_formal_milestones (client_visible).
//   5. Click Send → POST /diary/share → Worker signs photo URLs (7-day TTL),
//      calls app.share_diary_entry RPC, returns share_id.
//   6. Success modal shows recipient count + reassures "Will be delivered
//      within 1 minute via Coretel mail."
//
// Reused patterns: HOP submission contact picker, brand colours from T-167.
// =============================================================================

import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { X, Mail, Send, CheckCircle2, AlertTriangle, Flag, Users, ChevronDown, ChevronRight, Building2 } from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Spinner } from "@/components/ui";
import { format, parseISO } from "date-fns";

interface Props {
  diaryId:  string;
  onClose:  () => void;
  onSent?:  (shareId: string) => void;
}

interface ClientContact {
  contact_id:   string;
  full_name:    string;
  email:        string;
  role_title:   string | null;
  is_active:    boolean;
}

interface MilestoneOption {
  milestone_id:   string;
  milestone_code: string;
  name:           string | null;
  planned_date:   string | null;
  actual_date:    string | null;
  client_visible: boolean | null;
  payment_pct:    number | null;
}

interface DiaryHead {
  diary_id:       string;
  entry_date:     string;
  scope_done:     string | null;
  photo_count:    number;
  project_id:     string;
  project_code:   string;
  project_name:   string;
  client_id:      string | null;
  client_name:    string | null;
}

export default function ShareDiaryModal({ diaryId, onClose, onSent }: Props) {
  const user = useRequireAuth();
  const [pickedContacts, setPickedContacts] = useState<Set<string>>(new Set());
  const [milestoneId, setMilestoneId] = useState<string | null>(null);
  const [ccSelf, setCcSelf] = useState(true);
  // James, 9 Sep 2026: "working for Hardline but both to ventia so need to
  // update ventia on progress as well as hardline" — contacts from OTHER
  // clients can be added to a share. Collapsed by default so the normal
  // one-client share stays a two-tap job; expanded, other clients' contacts
  // are grouped by company so it's obvious whose people you're adding.
  const [showOtherClients, setShowOtherClients] = useState(false);
  const [openClientGroups, setOpenClientGroups] = useState<Set<string>>(new Set());
  const [sentResult, setSentResult] = useState<{ shareId: string; recipientCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // -- Load diary entry + project + client + photo count ----------------------
  const { data: head, isLoading: loadingHead } = useQuery({
    queryKey: ["share_diary_head", diaryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_diary_entries")
        .select(`
          diary_id, entry_date, scope_done, project_id,
          projects (
            project_code, project_name, client_id,
            clients ( legal_name )
          )
        `)
        .eq("diary_id", diaryId)
        .single();
      if (error) throw error;
      // Separate query for photo count — keeps the join above simple
      const { count } = await supabase
        .from("site_diary_photos")
        .select("photo_id", { count: "exact", head: true })
        .eq("diary_id", diaryId);
      const p = (data as any).projects;
      return {
        diary_id:     data.diary_id,
        entry_date:   data.entry_date,
        scope_done:   data.scope_done,
        photo_count:  count ?? 0,
        project_id:   data.project_id,
        project_code: p?.project_code,
        project_name: p?.project_name,
        client_id:    p?.client_id ?? null,
        client_name:  p?.clients?.legal_name ?? null,
      } as DiaryHead;
    },
  });

  // -- Load client contacts for the project's client --------------------------
  const { data: contacts = [], isLoading: loadingContacts } = useQuery({
    queryKey: ["share_diary_contacts", head?.client_id],
    enabled: !!head?.client_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_contacts")
        .select("contact_id, full_name, email, role_title, is_active")
        .eq("client_id", head!.client_id!)
        .eq("is_active", true)
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as ClientContact[];
    },
  });

  // -- Contacts from OTHER clients, grouped by company -------------------------
  // Backend already accepts any active contact_id (app.share_diary_entry
  // filters only on is_active), so this is purely a picker change. Loaded
  // lazily — only when the manager opens the "share outside" section.
  const { data: otherContacts = [], isLoading: loadingOther } = useQuery({
    queryKey: ["share_diary_other_contacts", head?.client_id ?? "none"],
    enabled: !!head && showOtherClients,
    queryFn: async () => {
      let q = supabase
        .from("client_contacts")
        .select("contact_id, full_name, email, role_title, is_active, client_id, clients(legal_name)")
        .eq("is_active", true)
        .order("full_name");
      if (head?.client_id) q = q.neq("client_id", head.client_id);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as (ClientContact & { client_id: string; clients: { legal_name: string } | null })[];
    },
  });

  const otherByClient = useMemo(() => {
    const groups = new Map<string, { name: string; contacts: (ClientContact & { client_id: string })[] }>();
    for (const c of otherContacts) {
      const name = c.clients?.legal_name ?? "Unknown client";
      if (!groups.has(c.client_id)) groups.set(c.client_id, { name, contacts: [] });
      groups.get(c.client_id)!.contacts.push(c);
    }
    return Array.from(groups.entries())
      .map(([id, g]) => ({ client_id: id, ...g }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [otherContacts]);

  // -- Load project milestones (client_visible only) --------------------------
  const { data: milestones = [] } = useQuery({
    queryKey: ["share_diary_milestones", head?.project_id],
    enabled: !!head?.project_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_formal_milestones")
        .select("milestone_id, milestone_code, name, planned_date, actual_date, client_visible, payment_pct")
        .eq("project_id", head!.project_id)
        .eq("client_visible", true)
        .order("milestone_code");
      if (error) throw error;
      return (data ?? []) as MilestoneOption[];
    },
  });

  // -- Active directors — auto-CC list so all directors get a copy ----------
  const { data: directors = [] } = useQuery({
    queryKey: ["share_diary_directors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("user_id, login_email, employees:employee_id(first_name, last_name)")
        .eq("role_name", "director")
        .eq("is_active", true);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // -- Share history for this entry -------------------------------------------
  const { data: history = [] } = useQuery({
    queryKey: ["share_diary_history", diaryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_site_diary_shares")
        .select("share_id, shared_at, shared_by_name, recipient_count, sent_count, pending_count, failed_count")
        .eq("diary_id", diaryId)
        .order("shared_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  // -- Send -------------------------------------------------------------------
  const sendMut = useMutation({
    mutationFn: async () => {
      if (!head) throw new Error("Diary not loaded");
      if (pickedContacts.size === 0 && !ccSelf) {
        throw new Error("Pick at least one contact, or enable CC me.");
      }
      // T-258 — when ccSelf ticked, CC ALL active directors (not just caller)
      // so the team has visibility on every client comm. Caller's own email
      // is included via the directors list since the caller IS a director
      // (manager+ role gate enforced upstream; dedupe defensively below).
      const ccEmails: string[] = [];
      if (ccSelf) {
        const seen = new Set<string>();
        for (const d of directors) {
          const email = d.login_email;
          if (email && !seen.has(email.toLowerCase())) {
            seen.add(email.toLowerCase());
            ccEmails.push(email);
          }
        }
        // Fallback: if directors query returned empty, at least CC self
        if (ccEmails.length === 0 && user?.login_email) {
          ccEmails.push(user.login_email);
        }
      }

      const res = await apiFetch("/diary/share", {
        method: "POST",
        body: JSON.stringify({
          diary_id:     diaryId,
          contact_ids:  Array.from(pickedContacts),
          cc_emails:    ccEmails,
          milestone_id: milestoneId,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? err.error ?? `share failed (${res.status})`);
      }
      const out = await res.json() as { share_id: string; recipient_count: number };
      return out;
    },
    onSuccess: (out) => {
      setSentResult({ shareId: out.share_id, recipientCount: out.recipient_count });
      setError(null);
      onSent?.(out.share_id);
    },
    onError: (e: any) => setError(e?.message ?? "Send failed"),
  });

  const toggleContact = (contactId: string) => {
    const next = new Set(pickedContacts);
    if (next.has(contactId)) next.delete(contactId);
    else next.add(contactId);
    setPickedContacts(next);
  };

  const renderContactRow = (c: ClientContact) => {
    const picked = pickedContacts.has(c.contact_id);
    return (
      <button
        key={c.contact_id}
        type="button"
        onClick={() => toggleContact(c.contact_id)}
        className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
          picked
            ? "bg-accent/10 border-accent/40"
            : "bg-ink-raised border-line hover:border-line-light"
        }`}
      >
        <div className="flex items-center gap-2">
          <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
            picked ? "bg-accent border-accent" : "border-line"
          }`}>
            {picked && <CheckCircle2 size={12} className="text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-text">{c.full_name}</div>
            <div className="text-[11px] text-text-muted font-mono truncate">
              {c.email}{c.role_title ? ` · ${c.role_title}` : ""}
            </div>
          </div>
        </div>
      </button>
    );
  };

  const canSend = useMemo(() => {
    if (!head) return false;
    if (sendMut.isPending) return false;
    if (pickedContacts.size === 0 && !ccSelf) return false;
    return true;
  }, [head, pickedContacts.size, ccSelf, sendMut.isPending]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={onClose}>
      <div
        className="bg-ink-card border-t md:border border-line w-full md:max-w-2xl md:rounded-lg max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-accent" />
            <h3 className="text-[16px] font-semibold text-text">Share diary update</h3>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loadingHead && <div className="py-10 flex justify-center"><Spinner /></div>}

          {head && !sentResult && (
            <>
              {/* Context */}
              <div className="bg-ink-raised border border-line rounded-md p-4">
                <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">Sharing</div>
                <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono text-accent text-[14px]">{head.project_code}</span>
                  <span className="text-[13px] text-text">{head.project_name}</span>
                </div>
                <div className="text-[12px] text-text-muted mt-1">
                  Entry for {format(parseISO(head.entry_date), "EEE dd MMM yyyy")} · {head.photo_count} photo{head.photo_count === 1 ? "" : "s"}
                </div>
                {head.client_name && (
                  <div className="text-[12px] text-text-muted mt-0.5">
                    Client: {head.client_name}
                  </div>
                )}
              </div>

              {/* Contact picker */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium flex items-center gap-1.5">
                    <Users size={11} /> Recipients
                    {pickedContacts.size > 0 && (
                      <span className="text-accent font-mono normal-case tracking-normal">({pickedContacts.size})</span>
                    )}
                  </div>
                </div>
                {!head.client_id ? (
                  <div className="p-3 rounded bg-warn/10 border border-warn/30 text-[12px] text-warn">
                    This project has no client linked — can't load contacts. Set the project's client first.
                  </div>
                ) : loadingContacts ? (
                  <div className="py-4 flex justify-center"><Spinner size={14} /></div>
                ) : contacts.length === 0 ? (
                  <div className="p-3 rounded bg-ink-raised border border-line text-[12px] text-text-muted">
                    No contacts on file for {head.client_name}. Add one from the client detail page first, or just CC yourself below for a test send.
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                    {contacts.map(renderContactRow)}
                  </div>
                )}

                {/* Share outside the project's client — e.g. a Hardline job
                    where Ventia also needs the progress update. Collapsed by
                    default; contacts grouped by company so it's clear whose
                    people are being added. */}
                <button
                  type="button"
                  onClick={() => setShowOtherClients(v => !v)}
                  className="mt-2 flex items-center gap-1.5 text-[12px] text-accent hover:underline"
                >
                  {showOtherClients ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <Building2 size={12} />
                  {head.client_name
                    ? `Share outside ${head.client_name} — add contacts from another company`
                    : "Add contacts from another company"}
                </button>
                {showOtherClients && (
                  <div className="mt-2 space-y-1.5 max-h-[220px] overflow-y-auto border border-line rounded-md p-2 bg-ink-card">
                    {loadingOther ? (
                      <div className="py-3 flex justify-center"><Spinner size={14} /></div>
                    ) : otherByClient.length === 0 ? (
                      <div className="text-[12px] text-text-muted px-1 py-1">
                        No other clients have contacts on file. Add them from the client detail pages.
                      </div>
                    ) : (
                      otherByClient.map(g => {
                        const open = openClientGroups.has(g.client_id);
                        const pickedInGroup = g.contacts.filter(c => pickedContacts.has(c.contact_id)).length;
                        return (
                          <div key={g.client_id}>
                            <button
                              type="button"
                              onClick={() => {
                                const next = new Set(openClientGroups);
                                if (open) next.delete(g.client_id);
                                else next.add(g.client_id);
                                setOpenClientGroups(next);
                              }}
                              className="w-full flex items-center gap-1.5 px-1 py-1.5 text-[12px] text-text font-medium"
                            >
                              {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                              <span>{g.name}</span>
                              <span className="text-text-muted font-normal">({g.contacts.length})</span>
                              {pickedInGroup > 0 && (
                                <span className="ml-auto text-accent font-mono text-[11px]">{pickedInGroup} picked</span>
                              )}
                            </button>
                            {open && (
                              <div className="space-y-1.5 pl-4 pb-1">
                                {g.contacts.map(renderContactRow)}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* CC self */}
              <label className="flex items-start gap-2 cursor-pointer p-3 rounded bg-ink-raised border border-line">
                <input
                  type="checkbox"
                  checked={ccSelf}
                  onChange={e => setCcSelf(e.target.checked)}
                  className="accent-accent mt-0.5"
                />
                <span className="text-[12px] text-text">
                  CC all directors ({directors.length})
                  <span className="block text-[11px] text-text-muted mt-0.5">
                    {directors.length > 0
                      ? `Sends a copy to: ${directors.map((d: any) => {
                          const name = [d.employees?.first_name, d.employees?.last_name].filter(Boolean).join(" ");
                          return name || d.login_email;
                        }).join(", ")}.`
                      : "Sends a copy to you for your records."}
                  </span>
                </span>
              </label>

              {/* Milestone callout (optional) */}
              {milestones.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium mb-2 flex items-center gap-1.5">
                    <Flag size={11} /> Milestone callout <span className="normal-case tracking-normal">(optional)</span>
                  </div>
                  <select
                    value={milestoneId ?? ""}
                    onChange={e => setMilestoneId(e.target.value || null)}
                    className="w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm outline-none focus:border-accent"
                  >
                    <option value="">— No milestone callout —</option>
                    {milestones.map(m => (
                      <option key={m.milestone_id} value={m.milestone_id}>
                        {m.milestone_code}
                        {m.name ? ` · ${m.name}` : ""}
                        {m.payment_pct ? ` · ${m.payment_pct}% payment` : ""}
                      </option>
                    ))}
                  </select>
                  <div className="text-[11px] text-text-muted mt-1.5">
                    Adds a "Milestone update" callout block at the top of the email.
                  </div>
                </div>
              )}

              {/* Share history (collapsed if none) */}
              {history.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium mb-2">
                    Previously shared
                  </div>
                  <div className="space-y-1">
                    {history.map((h: any) => (
                      <div key={h.share_id} className="text-[11px] text-text-muted bg-ink-raised border border-line rounded px-3 py-1.5 flex items-center gap-2">
                        <span className="font-mono">{format(parseISO(h.shared_at), "dd MMM HH:mm")}</span>
                        <span>by {h.shared_by_name}</span>
                        <span>·</span>
                        <span>{h.recipient_count} recipient{h.recipient_count === 1 ? "" : "s"}</span>
                        {h.sent_count > 0 && <span className="text-ok">· {h.sent_count} sent</span>}
                        {h.pending_count > 0 && <span className="text-warn">· {h.pending_count} pending</span>}
                        {h.failed_count > 0 && <span className="text-red-400">· {h.failed_count} failed</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="p-3 rounded bg-red-900/30 border border-red-900/50 text-[12px] text-red-200 flex items-start gap-2">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}

          {/* Success state */}
          {sentResult && (
            <div className="py-8 text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-ok/15 border border-ok/30 flex items-center justify-center">
                <CheckCircle2 size={22} className="text-ok" />
              </div>
              <div>
                <div className="text-[16px] font-semibold text-text">Queued for delivery</div>
                <div className="text-[13px] text-text-muted mt-1 max-w-sm mx-auto">
                  {sentResult.recipientCount} recipient{sentResult.recipientCount === 1 ? "" : "s"} will receive the update within ~1 minute via Coretel mail.
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        {!sentResult && (
          <div className="px-5 py-4 border-t border-line flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={sendMut.isPending}>
              Cancel
            </Button>
            <Button
              icon={Send}
              onClick={() => sendMut.mutate()}
              disabled={!canSend}
            >
              {sendMut.isPending ? "Sending…" : `Send to ${pickedContacts.size + (ccSelf ? directors.length : 0)}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
